import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { collection, doc, getDoc, getDocs, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import sanitizeFilename from "sanitize-filename";
import { auth, db, storage } from "../Context/firebaseConfig";
import { useAuth } from "../Context/AuthContext";
import useEntitlements from "../hooks/useEntitlements";
import EntitlementGate from "./shared/EntitlementGate";

import DOMPurify from "dompurify";

import {
  FaInstagram,
  FaYoutube,
  FaTwitch,
  FaTwitter,
  FaTiktok,
} from "react-icons/fa";

import {
  Sparkles,
  Workflow,
  HeartHandshake,
  AlertTriangle,
  Trophy,
  Play,
  Eye,
  Clock,
  X,
  Image,
  Palette,
  Star,
  BarChart3,
  BadgeCheck,
  LoaderCircle,
  Trash2,
  Upload,
} from "lucide-react";
import {
  buildEditorConversationStatistics,
  buildEditorOrderStatistics,
  buildEditorPortfolioStatistics,
  EDITOR_PROFILE_THEMES,
  getActiveEditorProfileBannerUrl,
  getActiveEditorProfileTheme,
  sanitizeEditorProfileTheme,
  sumEditorPortfolioVideoViews,
  validateEditorProfileBannerFile,
} from "../utils/editorProfileAppearance";
import { normalizeSocialUrl } from "../utils/socialLinks";

const PURPLE = "#9146FF";
const GREEN = "#84ff00";
const BG = "#07060d";
const LEGACY_VIDEO_FIELD_NAMES = [
  "videoOne",
  "videoTwo",
  "videoThree",
  "videoFour",
  "videoFive",
];

export default function EditorProfile() {
  const [editor, setEditor] = useState(null);
  const [error, setError] = useState(null);
  const [videoSaveError, setVideoSaveError] = useState("");
  const [notice, setNotice] = useState(null);
  const [selectedBannerFile, setSelectedBannerFile] = useState(null);
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState("");
  const [bannerSaving, setBannerSaving] = useState(false);
  const [themeSavingId, setThemeSavingId] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [originalVideoCount, setOriginalVideoCount] = useState(0);

  const [videoLinks, setVideoLinks] = useState(createEmptyVideoLinks(20));

  const [videos, setVideos] = useState([]);
  const [chatMetrics, setChatMetrics] = useState({
    messagesReceived: null,
    responseRate: null,
  });
  const [statsLoading, setStatsLoading] = useState(true);

  const [readMore, setReadMore] = useState(false);
  const { entitlements, portfolioVideoLimit } = useEntitlements(editor);
  const bannerInputRef = useRef(null);
  const { currentUser, activeUser } = useAuth();

  useEffect(() => {
    if (!notice) return undefined;

    const timeoutId = window.setTimeout(() => {
      setNotice(null);
    }, 3200);

    return () => window.clearTimeout(timeoutId);
  }, [notice]);

  useEffect(() => {
    if (!selectedBannerFile) {
      setBannerPreviewUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedBannerFile);
    setBannerPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [selectedBannerFile]);

  useEffect(() => {
    const fetchEditor = async (user) => {
      try {
        const docRef = doc(db, "Users", user.uid);

        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const editorData = {
            id: docSnap.id,
            ...docSnap.data(),
          };

          setEditor(editorData);

          const savedVideoLinks = docSnap.data().videoLinks;

          if (savedVideoLinks) {
            setVideoLinks(mergeVideoLinksWithDefaults(savedVideoLinks));
            setOriginalVideoCount(countFilledVideoLinks(savedVideoLinks));

            fetchVideoData(getOrderedVideoUrls(savedVideoLinks));
          }
        } else {
          setError("No editor found");
        }
      } catch (err) {
        console.error(err);
        setError("Failed to fetch editor");
      }
    };

    const fetchVideoData = async (urls) => {
      try {
        const videoData = await Promise.all(
          urls.map(async (url) => {
            if (!url) return null;

            const videoId = extractYouTubeId(url);

            if (!videoId) return null;

            const response = await fetch(
              `https://www.googleapis.com/youtube/v3/videos?id=${videoId}&key=${process.env.REACT_APP_YOUTUBE_API_KEY}&part=snippet,contentDetails,statistics`
            );

            const data = await response.json();

            if (data.items && data.items.length > 0) {
              return data.items[0];
            }

            return null;
          })
        );

        setVideos(videoData.filter((v) => v !== null));
      } catch (err) {
        console.error(err);
      }
    };

    const extractYouTubeId = (url) => {
      const regExp =
        /^https:\/\/www\.youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})(?=$|[&])/;

      const match = url.match(regExp);

      return match ? match[1] : null;
    };

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchEditor(user);
      } else {
        setError("No user logged in");
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadChatMetrics = async () => {
      if (!currentUser?.uid) {
        setChatMetrics({
          messagesReceived: null,
          responseRate: null,
        });
        setStatsLoading(false);
        return;
      }

      try {
        setStatsLoading(true);

        const chatRefsSnapshot = await getDocs(
          collection(db, "Users", currentUser.uid, "chats")
        );

        const chatDocs = await Promise.all(
          chatRefsSnapshot.docs.map(async (chatRefDoc) => {
            const chatDocSnapshot = await getDoc(doc(db, "chats", chatRefDoc.id));
            return chatDocSnapshot.exists() ? chatDocSnapshot.data() : null;
          })
        );

        setChatMetrics(
          buildEditorConversationStatistics(
            chatDocs.filter(Boolean),
            currentUser.uid
          )
        );
      } catch (statsError) {
        console.error("Failed to load editor conversation stats:", statsError);
        setChatMetrics({
          messagesReceived: null,
          responseRate: null,
        });
      } finally {
        setStatsLoading(false);
      }
    };

    loadChatMetrics();
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const sanitizedValue = DOMPurify.sanitize(value);

    setVideoLinks((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));
  };

  const showNotice = (message, type = "success") => {
    setNotice({ message, type });
  };

  const handleBannerFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const validationMessage = validateEditorProfileBannerFile(file);

    if (validationMessage) {
      showNotice(validationMessage, "error");
      event.target.value = "";
      return;
    }

    setSelectedBannerFile(file);
    showNotice("Banner ready to upload.");
  };

  const handleBannerUpload = async () => {
    const validationMessage = validateEditorProfileBannerFile(selectedBannerFile);

    if (validationMessage) {
      showNotice(validationMessage, "error");
      return;
    }

    try {
      setBannerSaving(true);

      const currentUser = auth.currentUser;

      if (!currentUser) {
        showNotice("Sign in again to update your banner.", "error");
        return;
      }

      const safeFileName =
        sanitizeFilename(selectedBannerFile.name) || `banner-${Date.now()}`;
      const storageRef = ref(
        storage,
        `profileBanners/${currentUser.uid}/${Date.now()}-${safeFileName}`
      );
      const uploadResult = await uploadBytes(storageRef, selectedBannerFile);
      const downloadURL = await getDownloadURL(uploadResult.ref);

      await updateDoc(doc(db, "Users", currentUser.uid), {
        "profileInfo.profileBannerUrl": downloadURL,
      });

      setEditor((prev) => ({
        ...prev,
        profileInfo: {
          ...(prev?.profileInfo || {}),
          profileBannerUrl: downloadURL,
        },
      }));
      setSelectedBannerFile(null);

      if (bannerInputRef.current) {
        bannerInputRef.current.value = "";
      }

      showNotice("Premium banner updated.");
    } catch (err) {
      console.error(err);
      showNotice("Failed to upload banner.", "error");
    } finally {
      setBannerSaving(false);
    }
  };

  const handleBannerRemove = async () => {
    try {
      setBannerSaving(true);

      const currentUser = auth.currentUser;

      if (!currentUser) {
        showNotice("Sign in again to update your banner.", "error");
        return;
      }

      await updateDoc(doc(db, "Users", currentUser.uid), {
        "profileInfo.profileBannerUrl": "",
      });

      setEditor((prev) => ({
        ...prev,
        profileInfo: {
          ...(prev?.profileInfo || {}),
          profileBannerUrl: "",
        },
      }));
      setSelectedBannerFile(null);

      if (bannerInputRef.current) {
        bannerInputRef.current.value = "";
      }

      showNotice("Banner removed.");
    } catch (err) {
      console.error(err);
      showNotice("Failed to remove banner.", "error");
    } finally {
      setBannerSaving(false);
    }
  };

  const handleThemeSelect = async (themeId) => {
    const normalizedThemeId = sanitizeEditorProfileTheme(themeId);

    if (!auth.currentUser) {
      showNotice("Sign in again to update your theme.", "error");
      return;
    }

    if (normalizedThemeId === editor?.profileInfo?.profileTheme) {
      return;
    }

    try {
      setThemeSavingId(normalizedThemeId);

      await updateDoc(doc(db, "Users", auth.currentUser.uid), {
        "profileInfo.profileTheme": normalizedThemeId,
      });

      setEditor((prev) => ({
        ...prev,
        profileInfo: {
          ...(prev?.profileInfo || {}),
          profileTheme: normalizedThemeId,
        },
      }));

      showNotice("Profile theme updated.");
    } catch (err) {
      console.error(err);
      showNotice("Failed to update theme.", "error");
    } finally {
      setThemeSavingId("");
    }
  };

  const handleSaveVideos = async () => {
    try {
      setVideoSaveError("");
      const filledVideoCount = countFilledVideoLinks(videoLinks);
      const maxAllowedSaves = Math.max(portfolioVideoLimit, originalVideoCount);

      if (filledVideoCount > maxAllowedSaves) {
        setVideoSaveError(
          `Your current plan allows ${portfolioVideoLimit} portfolio videos. Remove an extra video or upgrade to add more.`
        );
        return;
      }

      const currentUser = auth.currentUser;

      const docRef = doc(db, "Users", currentUser.uid);

      await updateDoc(docRef, {
        videoLinks: {
          ...videoLinks,
        },
      });

      setModalOpen(false);

      window.location.reload();
    } catch (err) {
      console.error(err);
      setError("Failed to save videos");
    }
  };

  const convertDuration = (duration) => {
    const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);

    const hours = parseInt(match[1]) || 0;
    const minutes = parseInt(match[2]) || 0;
    const seconds = parseInt(match[3]) || 0;

    return `${hours > 0 ? `${hours}:` : ""}${minutes}:${
      seconds < 10 ? "0" : ""
    }${seconds}`;
  };

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        {error}
      </div>
    );
  }

  if (!editor) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        Loading...
      </div>
    );
  }

  const QUESTIONS = [
    {
      icon: Workflow,
      label: "Best workflow",
      title: "How I run our collaboration",
      body: editor.profileInfo?.questions?.questionOne,
      accent: GREEN,
    },
    {
      icon: HeartHandshake,
      label: "Easy to work with",
      title: "How I make collab effortless",
      body: editor.profileInfo?.questions?.questionTwo,
      accent: PURPLE,
    },
    {
      icon: AlertTriangle,
      label: "Watch-outs",
      title: "Potential workflow pain points",
      body: editor.profileInfo?.questions?.questionThree,
      accent: "#fbbf24",
    },
    {
      icon: Trophy,
      label: "What I bring",
      title: "What I bring to your content",
      body: editor.profileInfo?.questions?.questionFour,
      accent: GREEN,
    },
  ];

  const featured = videos[0];
  const rail = videos.slice(1);
  const hasReachedVideoLimit = countFilledVideoLinks(videoLinks) >= portfolioVideoLimit;
  const activeTheme = getActiveEditorProfileTheme(editor);
  const activeBannerUrl = bannerPreviewUrl || getActiveEditorProfileBannerUrl(editor);
  const orderMetrics = buildEditorOrderStatistics(activeUser?.orders);
  const livePortfolioMetrics = {
    ...orderMetrics,
    ...chatMetrics,
    portfolioVideoViews: sumEditorPortfolioVideoViews(videos),
  };
  const portfolioStats = buildEditorPortfolioStatistics(editor, livePortfolioMetrics);
  const currentThemeId = sanitizeEditorProfileTheme(editor?.profileInfo?.profileTheme);

  return (
    <main
      className="min-h-screen overflow-x-hidden"
      style={{
        backgroundColor: BG,
      }}
    >
      {/* HERO */}
      <section className="relative">
        <div className="relative mx-auto max-w-6xl px-4 pt-10 pb-12 sm:px-6 sm:pt-16 sm:pb-20">
          <div
            className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-2xl sm:p-10"
            style={{
              boxShadow: "0 20px 80px rgba(0,0,0,0.5)",
              background: activeTheme.surface,
            }}
          >
            {activeBannerUrl && (
              <>
                <img
                  src={activeBannerUrl}
                  alt="profile banner"
                  className="absolute inset-0 h-full w-full object-cover opacity-35"
                />
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(7,6,13,0.22) 0%, rgba(7,6,13,0.84) 68%, rgba(7,6,13,0.96) 100%)",
                  }}
                />
              </>
            )}
            {/* glow */}
            <div
              className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full blur-3xl opacity-30"
              style={{
                background: activeTheme.glowA,
              }}
            />

            <div
              className="absolute top-20 -right-32 h-[480px] w-[480px] rounded-full blur-3xl opacity-20"
              style={{
                background: activeTheme.glowB,
              }}
            />

            <div className="relative flex flex-col items-center gap-6 text-center lg:flex-row lg:items-start lg:gap-10 lg:text-left">
              {/* avatar */}
              <div className="relative flex-shrink-0">
                <div
                  className="absolute inset-0 rounded-full blur-2xl opacity-70"
                  style={{
                    background: activeTheme.ring,
                  }}
                />

                <div
                  className="relative rounded-full p-1"
                  style={{
                    background: activeTheme.ring,
                  }}
                >
                  <img
                    src={editor.profileInfo?.profilePicUrl}
                    alt="profile"
                    className="h-28 w-28 rounded-full object-cover bg-[#0d0a18] sm:h-36 sm:w-36"
                  />
                </div>
              </div>

              {/* info */}
              <div className="min-w-0 flex-1">
                <h1 className="text-3xl font-extrabold tracking-tight text-white sm:text-5xl">
                  @{editor.profileInfo?.username}
                </h1>

                <p className="mt-2 text-base text-zinc-400 sm:text-lg">
                  {editor.profileInfo?.tagline}
                </p>

                {editor.profileInfo?.mainGame && (
                  <div className="mt-5 flex flex-wrap justify-center gap-2 lg:justify-start">
                    <span
                      className="inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold backdrop-blur"
                      style={{
                        background: `${activeTheme.secondary}15`,
                        border: `1px solid ${activeTheme.secondary}55`,
                        color: activeTheme.secondary,
                      }}
                    >
                      {editor.profileInfo?.mainGame}
                    </span>
                  </div>
                )}

                {/* socials */}
                <div className="mt-5 flex flex-wrap justify-center gap-2 lg:justify-start">
                  {editor.socials?.youtube && (
                    <SocialButton
                      icon={FaYoutube}
                      href={editor.socials.youtube}
                      platform="youtube"
                    />
                  )}

                  {editor.socials?.twitch && (
                    <SocialButton
                      icon={FaTwitch}
                      href={editor.socials.twitch}
                      platform="twitch"
                    />
                  )}

                  {editor.socials?.instagram && (
                    <SocialButton
                      icon={FaInstagram}
                      href={editor.socials.instagram}
                      platform="instagram"
                    />
                  )}

                  {editor.socials?.twitter && (
                    <SocialButton
                      icon={FaTwitter}
                      href={editor.socials.twitter}
                      platform="twitter"
                    />
                  )}

                  {editor.socials?.tiktok && (
                    <SocialButton
                      icon={FaTiktok}
                      href={editor.socials.tiktok}
                      platform="tiktok"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* about */}
            <div className="mt-8 rounded-2xl border border-white/10 bg-black/30 p-5 backdrop-blur sm:p-6">
              <div className="mb-3 flex items-center gap-2">
                <Sparkles
                  className="h-4 w-4"
                  style={{ color: activeTheme.secondary }}
                />

                <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-300">
                  About
                </h3>
              </div>

              <p
                className={`text-sm leading-relaxed text-zinc-300 sm:text-base ${
                  readMore ? "" : "line-clamp-3"
                }`}
              >
                {editor.profileInfo?.about}
              </p>

              <button
                onClick={() => setReadMore((s) => !s)}
                className="mt-2 text-xs font-semibold"
                style={{
                  color: activeTheme.secondary,
                }}
              >
                {readMore ? "Show less" : "Read more"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* PAID PROFILE TOOLS */}
      <section className="relative mx-auto max-w-6xl px-4 pb-16 sm:px-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
            Profile Growth Tools
          </h2>

          <p className="mt-1 text-sm text-zinc-400">
            Optional upgrades for stronger presentation and visibility.
          </p>
        </div>

        {notice && (
          <div
            className={`mb-4 rounded-2xl border px-4 py-3 text-sm font-medium ${
              notice.type === "error"
                ? "border-red-500/30 bg-red-500/10 text-red-100"
                : "border-white/10 bg-white/[0.05] text-white"
            }`}
          >
            {notice.message}
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          <EntitlementGate
            allowed={entitlements.customProfileBanner}
            title="Premium profile banner"
            description="Upload a premium banner to give your editor profile more visual polish."
          >
            <PremiumBannerPanel
              bannerUrl={activeBannerUrl || editor.profileInfo?.profileBannerUrl}
              selectedFileName={selectedBannerFile?.name || ""}
              saving={bannerSaving}
              onFileChange={handleBannerFileChange}
              onUpload={handleBannerUpload}
              onRemove={handleBannerRemove}
              inputRef={bannerInputRef}
              theme={activeTheme}
            />
          </EntitlementGate>

          <PremiumThemesPanel
            allowed={entitlements.premiumThemes}
            activeThemeId={currentThemeId}
            savingThemeId={themeSavingId}
            onSelectTheme={handleThemeSelect}
          />

          <EntitlementGate
            allowed={entitlements.featuredPlacement}
            title="Upgrade to become eligible"
            description="Featured placement gives your profile actual eligibility for boosted discovery surfaces."
          >
            <VisibilityStatusPanel entitlements={entitlements} />
          </EntitlementGate>

          <AnalyticsLitePanel
            allowed={entitlements.analyticsLite}
            stats={portfolioStats}
            loading={statsLoading}
          />

          <EntitlementGate
            allowed={entitlements.homepageSpotlight}
            title="Upgrade to Editor Elite"
            description="Editor Elite adds homepage spotlight eligibility, priority profile review, advanced customization, and a discounted Portfolio Review."
          >
            <EditorElitePanel entitlements={entitlements} />
          </EntitlementGate>
        </div>
      </section>

      {/* VIDEOS */}
      <section className="relative mx-auto max-w-6xl px-4 pb-16 sm:px-6">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Featured Work
            </h2>

            <p className="mt-1 text-sm text-zinc-400">
              Top-performing edits & creator collaborations
            </p>
            <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
              {countFilledVideoLinks(videoLinks)} of {portfolioVideoLimit} included videos used
            </p>
          </div>

          <div className="flex flex-col items-start gap-2 sm:items-end">
            <button
              onClick={() => {
                setVideoSaveError("");
                setModalOpen(true);
              }}
              className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-sm font-semibold text-white hover:border-white/30"
            >
              Edit Videos
            </button>
            {hasReachedVideoLimit && portfolioVideoLimit < 20 && (
              <Link
                to="/pricing"
                className="text-xs font-semibold text-zinc-400 transition hover:text-white"
              >
                Upgrade to add more videos
              </Link>
            )}
          </div>
        </div>

        {videos.length > 0 ? (
          <>
          {/* featured */}
          {featured && (
            <VideoCard
              large
              videoId={featured.id}
              thumbnail={featured.snippet.thumbnails.high.url}
              title={featured.snippet.title}
              views={featured.statistics.viewCount}
              duration={convertDuration(
                featured.contentDetails.duration
              )}
              date={new Date(
                featured.snippet.publishedAt
              ).toLocaleDateString()}
            />
          )}

          {/* rail */}
          <div className="mt-6 -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
            <div className="flex gap-4 pb-2">
              {rail.map((video) => (
                <VideoCard
                  key={video.id}
                  videoId={video.id}
                  thumbnail={video.snippet.thumbnails.medium.url}
                  title={video.snippet.title}
                  views={video.statistics.viewCount}
                  duration={convertDuration(
                    video.contentDetails.duration
                  )}
                  date={new Date(
                    video.snippet.publishedAt
                  ).toLocaleDateString()}
                />
              ))}
            </div>
          </div>
          </>
        ) : (
          <div className="rounded-3xl border border-dashed border-white/10 bg-white/[0.025] p-8 text-center">
            <p className="text-sm font-semibold text-white">No portfolio videos yet</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-zinc-400">
              Add YouTube links to showcase the edits you want creators to notice first.
            </p>
            <button
              onClick={() => setModalOpen(true)}
              className="mt-5 rounded-full px-5 py-2.5 text-sm font-bold text-black"
              style={{
                background: GREEN,
                boxShadow: `0 0 24px ${GREEN}80`,
              }}
            >
              Add Videos
            </button>
          </div>
        )}
      </section>

      {/* QUESTIONS */}
      <section className="relative mx-auto max-w-6xl px-4 pb-24 sm:px-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
            How we'd work together
          </h2>

          <p className="mt-1 text-sm text-zinc-400">
            Workflow, expectations, and what to expect.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {QUESTIONS.map((q) => (
            <QuestionCard key={q.title} q={q} />
          ))}
        </div>
      </section>

      {/* modal */}
      {modalOpen && (
        <EditVideosModal
          videoLinks={videoLinks}
          handleInputChange={handleInputChange}
          handleSaveVideos={handleSaveVideos}
          portfolioVideoLimit={portfolioVideoLimit}
          originalVideoCount={originalVideoCount}
          videoSaveError={videoSaveError}
          onClose={() => setModalOpen(false)}
        />
      )}
    </main>
  );
}

/* -------------------------------------------------------------------------- */
/* COMPONENTS */
/* -------------------------------------------------------------------------- */

function countFilledVideoLinks(links = {}) {
  return Object.values(links).filter((value) => String(value || "").trim()).length;
}

function getVideoFieldName(index) {
  if (index < LEGACY_VIDEO_FIELD_NAMES.length) {
    return LEGACY_VIDEO_FIELD_NAMES[index];
  }

  return `video${index + 1}`;
}

function createVideoFieldConfig(index) {
  return {
    name: getVideoFieldName(index),
    placeholder: `Video ${index + 1} URL`,
  };
}

function createEmptyVideoLinks(limit = 20) {
  return Array.from({ length: limit }).reduce((accumulator, _, index) => {
    accumulator[getVideoFieldName(index)] = "";
    return accumulator;
  }, {});
}

function mergeVideoLinksWithDefaults(videoLinks = {}, minimumSlots = 20) {
  const savedEntries = Object.entries(videoLinks || {});
  const totalSlots = Math.max(minimumSlots, savedEntries.length);

  return {
    ...createEmptyVideoLinks(totalSlots),
    ...videoLinks,
  };
}

function getVideoFieldIndex(name = "") {
  const legacyIndex = LEGACY_VIDEO_FIELD_NAMES.indexOf(name);

  if (legacyIndex !== -1) {
    return legacyIndex;
  }

  const numericMatch = name.match(/\d+/);

  if (numericMatch) {
    return Number(numericMatch[0]) - 1;
  }

  return Number.MAX_SAFE_INTEGER;
}

function getOrderedVideoUrls(videoLinks = {}) {
  return Object.entries(videoLinks)
    .sort(([leftKey], [rightKey]) => {
      return getVideoFieldIndex(leftKey) - getVideoFieldIndex(rightKey);
    })
    .map(([, value]) => value);
}

function PremiumBannerPanel({
  bannerUrl,
  selectedFileName,
  saving,
  onFileChange,
  onUpload,
  onRemove,
  inputRef,
  theme,
}) {
  return (
    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl sm:p-6">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <div
            className="mb-4 grid h-11 w-11 place-items-center rounded-2xl"
            style={{
              background: `${theme.accent}18`,
              border: `1px solid ${theme.accent}55`,
              color: theme.accent,
            }}
          >
            <Image className="h-5 w-5" />
          </div>
          <h3 className="text-base font-bold text-white">Premium profile banner</h3>
          <p className="mt-2 text-sm leading-relaxed text-zinc-400">
            Upload a real banner image that appears behind your editor profile hero.
          </p>
        </div>
        <span className="rounded-full border border-white/10 bg-white/[0.05] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-300">
          Editor Pro
        </span>
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/10 bg-black/20">
        <div
          className="relative h-36 w-full"
          style={{
            background: theme.surface,
          }}
        >
          {bannerUrl ? (
            <>
              <img
                src={bannerUrl}
                alt="editor banner preview"
                className="absolute inset-0 h-full w-full object-cover opacity-70"
              />
              <div className="absolute inset-0 bg-black/40" />
            </>
          ) : (
            <>
              <div
                className="absolute -left-10 top-0 h-36 w-36 rounded-full blur-3xl"
                style={{ background: theme.glowA }}
              />
              <div
                className="absolute right-0 top-4 h-28 w-28 rounded-full blur-3xl"
                style={{ background: theme.glowB }}
              />
            </>
          )}
          <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-3 px-4 py-3">
            <p className="text-sm font-semibold text-white">
              {selectedFileName || (bannerUrl ? "Current premium banner" : "No banner uploaded yet")}
            </p>
            {bannerUrl && (
              <BadgeCheck className="h-4 w-4 text-white/80" />
            )}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400">
          Banner image
        </label>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          onChange={onFileChange}
          className="mt-2 block w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-zinc-300 file:mr-4 file:rounded-full file:border-0 file:bg-white/10 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-white/15"
        />
        <p className="mt-2 text-xs text-zinc-500">
          JPG, PNG, WEBP, or GIF up to 5MB.
        </p>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onUpload}
          disabled={saving || !selectedFileName}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-black transition disabled:cursor-not-allowed disabled:opacity-60"
          style={{
            background: GREEN,
            boxShadow: `0 0 24px ${GREEN}50`,
          }}
        >
          {saving ? (
            <LoaderCircle className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          {saving ? "Uploading..." : "Upload banner"}
        </button>

        <button
          type="button"
          onClick={onRemove}
          disabled={saving || !bannerUrl}
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-4 py-2.5 text-sm font-semibold text-white transition hover:border-white/25 hover:bg-white/[0.08] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Trash2 className="h-4 w-4" />
          Remove banner
        </button>
      </div>
    </div>
  );
}

function PremiumThemesPanel({
  allowed,
  activeThemeId,
  savingThemeId,
  onSelectTheme,
}) {
  return (
    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl sm:p-6">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <div
            className="mb-4 grid h-11 w-11 place-items-center rounded-2xl"
            style={{
              background: `${PURPLE}18`,
              border: `1px solid ${PURPLE}55`,
              color: PURPLE,
            }}
          >
            <Palette className="h-5 w-5" />
          </div>
          <h3 className="text-base font-bold text-white">Premium profile themes</h3>
          <p className="mt-2 text-sm leading-relaxed text-zinc-400">
            {allowed
              ? "Choose a premium theme for your private and public editor profile hero."
              : "Locked themes help your profile stand out with more polish."}
          </p>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-3">
        {EDITOR_PROFILE_THEMES.map((theme) => {
          const isActive = activeThemeId === theme.id;
          const isSaving = savingThemeId === theme.id;

          return (
            <button
              type="button"
              key={theme.id}
              onClick={() => allowed && onSelectTheme(theme.id)}
              disabled={!allowed || Boolean(savingThemeId)}
              className={`rounded-2xl border p-3 text-left transition ${
                allowed
                  ? isActive
                    ? "border-white/30 bg-white/[0.07]"
                    : "border-white/15 bg-white/[0.05] hover:border-white/25"
                  : "border-white/10 bg-black/20 opacity-60"
              }`}
            >
            <div
              className="mb-3 h-10 rounded-xl"
              style={{
                background: theme.surface,
              }}
            />
            <p className="text-xs font-semibold text-white">{theme.name}</p>
            <p className="mt-1 text-[11px] text-zinc-500">
              {!allowed
                ? "Locked"
                : isSaving
                ? "Saving..."
                : isActive
                ? "Active"
                : "Select"}
            </p>
            </button>
          );
        })}
      </div>

      {!allowed && (
        <Link
          to="/pricing"
          className="mt-5 inline-flex items-center rounded-full border border-white/10 bg-white/[0.05] px-4 py-2.5 text-sm font-semibold text-white transition hover:border-white/25 hover:bg-white/[0.08]"
        >
          Upgrade for themes
        </Link>
      )}
    </div>
  );
}

function StatusRow({ label, value, tone = "default" }) {
  const toneClasses =
    tone === "success"
      ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-100"
      : tone === "accent"
      ? "border-sky-500/20 bg-sky-500/10 text-sky-100"
      : "border-white/10 bg-black/20 text-white";

  return (
    <div className={`flex items-center justify-between gap-3 rounded-2xl border px-3 py-3 ${toneClasses}`}>
      <p className="text-sm text-zinc-300">{label}</p>
      <p className="text-sm font-bold">{value}</p>
    </div>
  );
}

function VisibilityStatusPanel({ entitlements }) {
  return (
    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl sm:p-6">
      <div
        className="mb-4 grid h-11 w-11 place-items-center rounded-2xl"
        style={{
          background: `${GREEN}18`,
          border: `1px solid ${GREEN}55`,
          color: GREEN,
        }}
      >
        <Star className="h-5 w-5" />
      </div>
      <h3 className="text-base font-bold text-white">Discovery visibility</h3>
      <p className="mt-2 text-sm leading-relaxed text-zinc-400">
        Your upgraded profile is eligible for the public Featured Editor badge and stronger discovery surfaces while the subscription stays active.
      </p>

      <div className="mt-5 space-y-2">
        <StatusRow label="Featured Editor badge" value="Active" tone="success" />
        <StatusRow
          label="Higher search placement"
          value="Eligible"
          tone="success"
        />
        <StatusRow
          label="Featured rotation priority"
          value={
            entitlements.higherFeaturedRotationPriority ? "Highest" : "Standard"
          }
          tone={entitlements.higherFeaturedRotationPriority ? "accent" : "default"}
        />
      </div>
    </div>
  );
}

function AnalyticsLitePanel({ allowed, stats, loading }) {
  const cards = allowed
    ? stats
    : [
        { label: "Profile views", value: "Locked", isLive: false },
        { label: "Portfolio video views", value: "Locked", isLive: false },
        { label: "Messages received", value: "Locked", isLive: false },
        { label: "Orders completed", value: "Locked", isLive: false },
        { label: "Returning creators", value: "Locked", isLive: false },
        { label: "Response rate", value: "Locked", isLive: false },
      ];

  return (
    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl sm:p-6">
      <div
        className="mb-4 grid h-11 w-11 place-items-center rounded-2xl"
        style={{
          background: `${GREEN}18`,
          border: `1px solid ${GREEN}55`,
          color: GREEN,
        }}
      >
        <BarChart3 className="h-5 w-5" />
      </div>
      <h3 className="text-base font-bold text-white">Portfolio Statistics</h3>
      <p className="mt-2 text-sm leading-relaxed text-zinc-400">
        {allowed
          ? "Live portfolio counts show up here now, and tracked business metrics can fill in as analytics data starts landing."
          : "Preview profile, portfolio, message, and order performance once Portfolio Statistics is unlocked."}
      </p>
      {allowed && loading && (
        <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">
          Refreshing live stats...
        </p>
      )}

      <div className="mt-5 grid gap-2 sm:grid-cols-3">
        {cards.map((card) => (
          <div key={card.label} className="rounded-2xl border border-white/10 bg-black/20 p-3">
            <p className="text-[11px] uppercase tracking-wider text-zinc-500">{card.label}</p>
            <p className="mt-1 text-sm font-bold text-white">{card.value}</p>
            {allowed && (
              <p className="mt-1 text-[11px] text-zinc-500">
                {card.isLive ? "Live" : "Tracking soon"}
              </p>
            )}
          </div>
        ))}
      </div>

      {!allowed && (
        <Link
          to="/pricing"
          className="mt-5 inline-flex items-center rounded-full border border-white/10 bg-white/[0.05] px-4 py-2.5 text-sm font-semibold text-white transition hover:border-white/25 hover:bg-white/[0.08]"
        >
          Upgrade for statistics
        </Link>
      )}
    </div>
  );
}

function EditorElitePanel({ entitlements }) {
  return (
    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl sm:p-6">
      <div
        className="mb-4 grid h-11 w-11 place-items-center rounded-2xl"
        style={{
          background: `${PURPLE}18`,
          border: `1px solid ${PURPLE}55`,
          color: PURPLE,
        }}
      >
        <BadgeCheck className="h-5 w-5" />
      </div>
      <h3 className="text-base font-bold text-white">Editor Elite extras</h3>
      <p className="mt-2 text-sm leading-relaxed text-zinc-400">
        Elite keeps your profile ready for top placement, faster reviews, and hands-on portfolio improvement.
      </p>

      <div className="mt-5 space-y-2">
        <StatusRow label="Homepage spotlight eligibility" value="Eligible" tone="success" />
        <StatusRow label="Priority profile review" value="Included" tone="success" />
        <StatusRow
          label="Advanced profile customization"
          value={entitlements.advancedProfileCustomization ? "Unlocked" : "Locked"}
          tone={entitlements.advancedProfileCustomization ? "accent" : "default"}
        />
        <StatusRow
          label="Discounted Portfolio Review"
          value={entitlements.discountedPortfolioCritique ? "Available" : "Locked"}
          tone={entitlements.discountedPortfolioCritique ? "accent" : "default"}
        />
      </div>

      <Link
        to="/pricing"
        className="mt-5 inline-flex items-center rounded-full border border-white/10 bg-white/[0.05] px-4 py-2.5 text-sm font-semibold text-white transition hover:border-white/25 hover:bg-white/[0.08]"
      >
        View pricing
      </Link>
    </div>
  );
}

function SocialButton({ icon: Icon, href, platform }) {
  const socialUrl = normalizeSocialUrl(platform, href);
  if (!socialUrl) return null;

  return (
    <a
      href={socialUrl}
      target="_blank"
      rel="noreferrer"
      className="grid h-11 w-11 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-zinc-300 backdrop-blur transition hover:scale-110 hover:border-white/30 hover:text-white"
      style={{
        boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
      }}
    >
      <Icon className="h-4 w-4" />
    </a>
  );
}

function VideoCard({
  thumbnail,
  title,
  views,
  duration,
  date,
  videoId,
  large = false,
}) {
  return (
    <a
      href={`https://www.youtube.com/watch?v=${videoId}`}
      target="_blank"
      rel="noreferrer"
      className={`group relative block overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] transition hover:-translate-y-1 hover:border-white/20 ${
        large
          ? "aspect-video w-full"
          : "aspect-video w-[78vw] sm:w-[340px] flex-shrink-0"
      }`}
      style={{
        boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
      }}
    >
      <img
        src={thumbnail}
        alt={title}
        className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />

      <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-md bg-black/70 px-2 py-1 text-[11px] font-medium text-white backdrop-blur">
        <Clock className="h-3 w-3" />
        {duration}
      </span>

      <div className="absolute inset-0 grid place-items-center">
        <div
          className="grid h-14 w-14 place-items-center rounded-full backdrop-blur-md transition group-hover:scale-110"
          style={{
            background: `${GREEN}E0`,
            boxShadow: `0 0 30px ${GREEN}80`,
          }}
        >
          <Play className="h-6 w-6 fill-black text-black" />
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 p-4">
        <h4
          className={`line-clamp-2 font-semibold text-white ${
            large ? "text-lg sm:text-xl" : "text-sm"
          }`}
        >
          {title}
        </h4>

        <div className="mt-1.5 flex items-center gap-3 text-[11px] text-zinc-300">
          <span className="inline-flex items-center gap-1">
            <Eye className="h-3 w-3" />
            {views}
          </span>

          <span>•</span>

          <span>{date}</span>
        </div>
      </div>
    </a>
  );
}

function QuestionCard({ q }) {
  const Icon = q.icon;

  return (
    <div
      className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl transition hover:-translate-y-0.5 hover:border-white/20 sm:p-6"
      style={{
        boxShadow: "0 10px 40px rgba(0,0,0,0.4)",
      }}
    >
      <div
        className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full blur-3xl opacity-30 transition group-hover:opacity-50"
        style={{
          background: q.accent,
        }}
      />

      <div className="flex items-start gap-4">
        <div
          className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-2xl"
          style={{
            background: `${q.accent}18`,
            border: `1px solid ${q.accent}55`,
            color: q.accent,
          }}
        >
          <Icon className="h-5 w-5" />
        </div>

        <div className="min-w-0">
          <p
            className="text-[11px] font-semibold uppercase tracking-wider"
            style={{
              color: q.accent,
            }}
          >
            {q.label}
          </p>

          <h4 className="mt-1 text-base font-bold text-white sm:text-lg">
            {q.title}
          </h4>

          <p className="mt-2 text-sm leading-relaxed text-zinc-400">
            {q.body}
          </p>
        </div>
      </div>
    </div>
  );
}

function EditVideosModal({
  videoLinks,
  handleInputChange,
  handleSaveVideos,
  portfolioVideoLimit,
  originalVideoCount,
  videoSaveError,
  onClose,
}) {
  const filledVideoCount = countFilledVideoLinks(videoLinks);
  const editableLimit = Math.max(portfolioVideoLimit, originalVideoCount);
  const visibleSlotCount = Math.max(editableLimit, 5);
  const videoFields = Array.from({ length: visibleSlotCount }, (_, index) =>
    createVideoFieldConfig(index)
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-t-3xl border border-white/10 bg-[#0d0a18] p-6 sm:rounded-3xl"
        style={{
          boxShadow: `0 0 60px ${PURPLE}40`,
        }}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-full bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>

        <h3 className="text-xl font-bold text-white">
          Add your top edited videos
        </h3>

        <p className="mt-1 text-sm text-zinc-400">
          Only upload videos you've edited. Your current plan includes{" "}
          {portfolioVideoLimit} portfolio videos.
        </p>

        {videoSaveError && (
          <div className="mt-4 rounded-2xl border border-red-400/20 bg-red-500/10 p-3 text-sm text-red-100">
            {videoSaveError}
          </div>
        )}

        <div className="mt-5 space-y-3">
              {videoFields.map((field, index) => {
                const value = videoLinks[field.name] || "";
            const hasValue = Boolean(String(value).trim());
            const isBeyondIncludedSlots = index >= editableLimit;
            const isAddingPastLimit = !hasValue && filledVideoCount >= editableLimit;
            const locked = isBeyondIncludedSlots || isAddingPastLimit;

            return (
              <div key={field.name}>
                <input
                  type="text"
                  name={field.name}
                  value={value}
                  onChange={handleInputChange}
                  placeholder={locked ? "Upgrade to add more portfolio videos" : field.placeholder}
                  disabled={locked}
                  className={`w-full rounded-xl border px-4 py-3 text-sm text-white outline-none transition ${
                    locked
                      ? "cursor-not-allowed border-white/5 bg-white/[0.02] text-zinc-500 placeholder:text-zinc-600"
                      : "border-white/10 bg-white/[0.04] placeholder:text-zinc-500 focus:border-white/25"
                  }`}
                />
                {locked && (
                  <Link
                    to="/pricing"
                    className="mt-1.5 inline-block text-xs font-semibold text-zinc-400 transition hover:text-white"
                  >
                    Upgrade to unlock this slot
                  </Link>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-zinc-300 hover:bg-white/10"
          >
            Cancel
          </button>

          <button
            onClick={handleSaveVideos}
            className="flex-1 rounded-xl px-4 py-3 text-sm font-bold text-black"
            style={{
              background: GREEN,
              boxShadow: `0 0 24px ${GREEN}80`,
            }}
          >
            Save Videos
          </button>
        </div>
      </div>
    </div>
  );
}
